import { useInfiniteQuery } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import * as CodeRepositoriesService from '../services/CodeRepositoriesService';

const DEFAULT_LIMIT = 10;

// Query keys
export const codeRepositoriesKeys = {
    all: ['codeRepositories'] as const,
    lists: () => [...codeRepositoriesKeys.all, 'list'] as const,
    list: (filters: Record<string, string>) => [...codeRepositoriesKeys.lists(), { filters }] as const,
};

// Hooks
export const useCodeRepositoriesQuery = (filters: Record<string, string> = {}) => {
    return useInfiniteQuery({
        queryKey: codeRepositoriesKeys.list(filters),
        queryFn: ({ pageParam }: { pageParam: string | null }) =>
            CodeRepositoriesService.fetchRecords({
                filter: filters,
                limit: DEFAULT_LIMIT,
                nextToken: pageParam,
            }),
        getNextPageParam: (lastPage) => {
            if (!lastPage || !lastPage.nextToken) return undefined;
            return lastPage.nextToken;
        },
        initialPageParam: null as string | null,
    });
};

// Global filter state (shared across components)
let globalFilters: Record<string, string> = {};
let filterListeners: Set<(filters: Record<string, string>) => void> = new Set();

const notifyFilterChange = (filters: Record<string, string>) => {
    globalFilters = filters;
    filterListeners.forEach(listener => listener(filters));
};

export const useCodeRepositoriesFilter = () => {
    const [filters, setFilters] = useState<Record<string, string>>(globalFilters);

    useEffect(() => {
        const listener = (newFilters: Record<string, string>) => {
            setFilters(newFilters);
        };
        filterListeners.add(listener);
        return () => {
            filterListeners.delete(listener);
        };
    }, []);

    const handleSetFilters = (newFilters: Record<string, string>) => {
        notifyFilterChange(newFilters);
    };

    const handleClearFilters = () => {
        notifyFilterChange({});
    };

    return {
        filters,
        handleSetFilters,
        handleClearFilters,
    };
};
